/* eslint-disable @typescript-eslint/no-explicit-any */
import { InferType } from 'yup';
import { editCareTeamSchema } from '@views/pages/Patient/components/CareTeam/components/schemas';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  usePartnerShortQuery,
  usePatientCareTeamQuery,
  usePatientQuery,
  usePracticeShortQuery,
  useProvidersShortQuery,
  useUpdateCareTeamMutation,
  useUserListShortQuery,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useMediaQuery } from '@mui/material';

type IFormShape = InferType<typeof editCareTeamSchema>;

const initialValues: IFormShape = {
  partner: '',
  practice: '',
  provider: '',
  provider_type: 'Internal',
  geropsych: '',
  cardiology: '',
  palliativeCare: '',
  neurology: '',
  socialWorker: '',
  careNavigator: '',
  home_health_provider_id: '',
};

const findFirstMatchingOption = (options: { id: string }[], ids: string[]) => {
  for (const id of ids) {
    const found = options.find(option => option.id === id);
    if (found) return found;
  }
  return undefined;
};

export const useEditCareTeam = (patient_id: string) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  // const [isConfirmPCPChangeOpen, setIsConfirmPCPChangeOpen] = useState(false);

  const { data: careTeamData } = usePatientCareTeamQuery(patient_id);
  const { data: patient } = usePatientQuery({ patient_id });

  const isXSMSizeDP = useMediaQuery('(max-height: 768px)');
  const isSMSizeDP = useMediaQuery('(max-height: 900px)');
  const isMDSizeDP = useMediaQuery('(max-height: 1080px)');

  const hasCanvasId = !!patient?.additional_information.canvas_id;

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    resolver: yupResolver<IFormShape, any, any>(editCareTeamSchema, {
      context: { hasCanvasId },
    }),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    getValues,
    reset,
  } = formMethods;

  const { data: partnerOptions = [], isLoading: partnersLoading } =
    usePartnerShortQuery();
  const { data: practiceOptions = [], isLoading: practicesLoading } =
    usePracticeShortQuery({
      record_id: getValues('partner'),
    });
  const { data: providersOptions = [], isLoading: providersLoading } =
    useProvidersShortQuery({});
  const { data: careNavigatorOptions = [] } = useUserListShortQuery({});

  const { mutateAsync, isPending } = useUpdateCareTeamMutation(patient_id);

  useEffect(() => {
    if (
      careTeamData?.partner_id &&
      !partnersLoading &&
      !practicesLoading &&
      !providersLoading &&
      isOpen
    ) {
      const selectedPartner = findFirstMatchingOption(partnerOptions, [
        // getValues('partner'),
        careTeamData?.partner_id,
      ]);
      const selectedPractice = findFirstMatchingOption(practiceOptions, [
        // getValues('practice'),
        careTeamData?.practice_id,
      ]);

      const selectedAssignedProvider = findFirstMatchingOption(
        providersOptions,
        [
          // getValues('assignedProvider') || '',
          careTeamData?.primary_care_provider?.id,
        ],
      );
      const selectedGeropsych = findFirstMatchingOption(providersOptions, [
        // getValues('geropsych') || '',
        careTeamData?.geropsych_provider?.id,
      ]);
      const selectedCardiology = findFirstMatchingOption(providersOptions, [
        // getValues('cardiology') || '',
        careTeamData?.cardiology_provider?.id,
      ]);
      const selectedPalliativeCare = findFirstMatchingOption(providersOptions, [
        // getValues('palliativeCare') || '',
        careTeamData?.palliative_care_provider?.id,
      ]);
      const selectedNeurology = findFirstMatchingOption(providersOptions, [
        // getValues('palliativeCare') || '',
        careTeamData?.neurology_provider?.id,
      ]);
      const selectedHomeHealth = findFirstMatchingOption(providersOptions, [
        // getValues('palliativeCare') || '',
        careTeamData?.home_health_provider?.id,
      ]);
      const selectedSocialWorker = findFirstMatchingOption(providersOptions, [
        careTeamData?.social_worker_provider?.id,
      ]);
      const selectedCareNavigator = findFirstMatchingOption(
        careNavigatorOptions,
        [
          // getValues('careNavigator') || '',
          careTeamData?.care_navigator?.id,
        ],
      );

      reset({
        partner: selectedPartner?.id ?? '',
        practice: selectedPractice?.id ?? '',
        provider: selectedAssignedProvider?.id,
        geropsych: selectedGeropsych?.id ?? null,
        cardiology: selectedCardiology?.id ?? null,
        palliativeCare: selectedPalliativeCare?.id ?? null,
        neurology: selectedNeurology?.id ?? null,
        socialWorker: selectedSocialWorker?.id ?? null,
        careNavigator: selectedCareNavigator?.id ?? null,
        home_health_provider_id: selectedHomeHealth?.id ?? null,
        provider_type: careTeamData.provider_type,
        provider_phone:
          careTeamData.provider_type === 'External'
            ? careTeamData.primary_care_provider.phone_number
            : '',
        provider_last_name:
          careTeamData.provider_type === 'External'
            ? careTeamData.primary_care_provider.last_name
            : '',
        provider_first_name:
          careTeamData.provider_type === 'External'
            ? careTeamData.primary_care_provider.first_name
            : '',
      });
    }
  }, [careTeamData, isOpen]);

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: IFormShape) => {
    try {
      await mutateAsync({
        ...(!hasCanvasId && {
          ...(values.provider && {
            provider_id: values.provider,
          }),
          provider_type: values.provider_type,
          provider_first_name: values.provider_first_name,
          provider_last_name: values.provider_last_name,
          provider_phone: values.provider_phone,
          practice_id: values.practice,
          geropsych_provider_id: values.geropsych,
          cardiology_provider_id: values.cardiology,
          palliative_care_provider_id: values.palliativeCare,
          neurology_provider_id: values.neurology,
          home_health_provider_id: values.home_health_provider_id,
        }),
        social_worker_provider_id: values.socialWorker,
        user_id: values.careNavigator || null,
      });

      setIsSaveChangesModalOpen(false);
      // setIsConfirmPCPChangeOpen(false);
      setIsOpen(false);
      reset(values);
      toast.success('Care team changes saved');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const submitForm = handleSubmit(submitHandler);

  const onSubmit = async (
    event?: BaseSyntheticEvent<object, any, any> | undefined,
  ) => {
    //This is the logic for the confirmation modal, in case we decide to reinstate it

    // if (
    //   (formMethods.formState.dirtyFields.provider &&
    //     formMethods.getValues('provider_type') === 'Internal') ||
    //   (formMethods.getValues('provider_type') === 'External' &&
    //     (formMethods.formState.dirtyFields.provider_first_name ||
    //       formMethods.formState.dirtyFields.provider_last_name))
    // )
    //   setIsConfirmPCPChangeOpen(true);
    // else submitForm(event);

    event?.preventDefault?.();

    if (hasCanvasId) {
      await submitHandler(getValues());
      return;
    }

    submitForm(event);
  };

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = () => {
    onSubmit();
  };

  // const handleCloseConfirmPCPModal = () => {
  //   setIsConfirmPCPChangeOpen(false);
  // };

  const formValues = useWatch({ control: formMethods.control });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional: updating RHF context for cross-field yup validation, this is the documented pattern
    formMethods.control._options.context = {
      values: formValues,
      hasCanvasId,
    };
  }, [hasCanvasId, formValues]);

  return {
    isOpen,
    handleClose,
    handleOpen,
    isSubmitting,
    formMethods,
    onSubmit,
    partnerOptions: partnerOptions.map(p => {
      return { value: p.id, label: p.name };
    }),
    careTeamData,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
    modalHeight: isXSMSizeDP
      ? 630
      : isSMSizeDP
        ? 770
        : isMDSizeDP
          ? 840
          : undefined,
    hasCanvasId,
  };
};
